import { http, URL } from "./http.service";

// Check if customer can review a product
export const checkCustomerCanReview = async (businessUuid, productId, customerId) => {
  try {
    const resp = await http.get(
      `${URL}/reviews/${businessUuid}/${productId}/can-review/${customerId}`
    );
    return {
      success: true,
      ...resp.data
    };
  } catch (error) {
    console.error('Error checking review eligibility:', error);
    return {
      success: false,
      canReview: false,
      message: 'Unable to check review eligibility'
    };
  }
};

// Upload review photo (single photo - for backward compatibility)
export const uploadReviewPhoto = async (businessUuid, reviewId, photoFile) => {
  try {
    const formData = new FormData();
    formData.append('photo', photoFile);
    
    const resp = await http.post(
      `${URL}/reviews/${businessUuid}/${reviewId}/upload-photo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error uploading review photo:', error);
    return {
      success: false,
      error: 'Failed to upload photo'
    };
  }
};

// Add review with multiple images (up to 4)
export const addReviewWithImages = async (businessUuid, productId, formData) => {
  try {
    const resp = await http.post(
      `${URL}/reviews/${businessUuid}/${productId}/add-with-images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error adding review with images:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to add review with images'
    };
  }
};

// Upload additional photos to existing review (multiple photos)
export const uploadReviewPhotos = async (businessUuid, reviewId, files) => {
  try {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('reviewImages', file);
    });

    const resp = await http.post(
      `${URL}/reviews/${businessUuid}/${reviewId}/upload-photos`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error uploading review photos:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to upload photos'
    };
  }
};

// Remove photo from review
export const removeReviewPhoto = async (businessUuid, reviewId, photoIndex) => {
  try {
    const resp = await http.delete(
      `${URL}/reviews/${businessUuid}/${reviewId}/remove-photo/${photoIndex}`
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error removing review photo:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to remove photo'
    };
  }
};

// Get pending reviews for admin
export const getPendingReviews = async (businessUuid, page = 1, limit = 20) => {
  try {
    const resp = await http.get(
      `${URL}/reviews/${businessUuid}/pending?page=${page}&limit=${limit}`
    );
    return {
      success: true,
      ...resp.data
    };
  } catch (error) {
    console.error('Error fetching pending reviews:', error);
    return {
      success: false,
      reviews: [],
      pagination: {}
    };
  }
};

// Approve a review
export const approveReview = async (businessUuid, reviewId, adminId, verificationNotes = '') => {
  try {
    const resp = await http.post(
      `${URL}/reviews/${businessUuid}/${reviewId}/approve`,
      { adminId, verificationNotes }
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error approving review:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to approve review'
    };
  }
};

// Reject a review
export const rejectReview = async (businessUuid, reviewId, adminId, rejectionReason) => {
  try {
    const resp = await http.post(
      `${URL}/reviews/${businessUuid}/${reviewId}/reject`,
      { adminId, rejectionReason }
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error rejecting review:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to reject review'
    };
  }
};

// Get all reviews for admin
export const getAllReviews = async (businessUuid, params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const resp = await http.get(
      `${URL}/reviews/${businessUuid}/all?${queryString}`
    );
    return {
      success: true,
      ...resp.data
    };
  } catch (error) {
    console.error('Error fetching all reviews:', error);
    return {
      success: false,
      reviews: [],
      pagination: {}
    };
  }
};

// Delete a review (soft delete)
export const deleteReview = async (businessUuid, reviewId, customerId) => {
  try {
    const resp = await http.delete(
      `${URL}/reviews/${businessUuid}/${reviewId}`,
      { data: { customerId } }
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error deleting review:', error);
    return {
      success: false,
      error: 'Failed to delete review'
    };
  }
};

// Get store review settings
export const getStoreReviewSettings = async (businessUuid) => {
  try {
    const resp = await http.get(`${URL}/reviews/${businessUuid}/settings`);
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error fetching review settings:', error);
    return {
      success: false,
      data: {
        requiresVerification: false,
        autoApproveVerifiedPurchases: true,
        notifyAdminOnNewReview: true,
        minimumRatingForAutoApprove: 3,
        maxReviewsPerDay: 10,
        maxPhotosPerReview: 4
      }
    };
  }
};

// Update store review settings
export const updateStoreReviewSettings = async (businessUuid, settings) => {
  try {
    const resp = await http.put(
      `${URL}/reviews/${businessUuid}/settings`,
      settings
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error updating review settings:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to update review settings'
    };
  }
};

// review.service.js - Fix bulkApproveReviews

export const bulkApproveReviews = async (businessUuid, reviewIds, adminId) => {
  try {
    console.log('Bulk approve - Input params:', { businessUuid, reviewIds, adminId });
    
    // Ensure reviewIds is an array
    if (!Array.isArray(reviewIds)) {
      console.error('reviewIds is not an array:', reviewIds);
      return {
        success: false,
        error: 'Review IDs must be an array'
      };
    }
    
    // Send as JSON, NOT FormData
    const requestData = { 
      reviewIds: reviewIds, 
      adminId: adminId 
    };
    
    console.log('Sending request data:', requestData);
    
    const resp = await http.post(
      `${URL}/reviews/${businessUuid}/bulk-approve`,
      requestData,
      {
        headers: {
          'Content-Type': 'application/json', // Make sure this is set
        },
      }
    );
    
    console.log('Bulk approve API response:', resp.data);
    
    return {
      success: true,
      ...resp.data
    };
  } catch (error) {
    console.error('Error bulk approving reviews:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to bulk approve reviews',
      modifiedCount: 0,
      processed: 0
    };
  }
};

// Same for bulkRejectReviews
export const bulkRejectReviews = async (businessUuid, reviewIds, adminId, rejectionReason) => {
  try {
    console.log('Bulk reject - Input params:', { businessUuid, reviewIds, adminId, rejectionReason });
    
    if (!Array.isArray(reviewIds)) {
      return {
        success: false,
        error: 'Review IDs must be an array'
      };
    }
    
    const requestData = { 
      reviewIds: reviewIds, 
      adminId: adminId,
      rejectionReason: rejectionReason
    };
    
    const resp = await http.post(
      `${URL}/reviews/${businessUuid}/bulk-reject`,
      requestData,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    return {
      success: true,
      ...resp.data
    };
  } catch (error) {
    console.error('Error bulk rejecting reviews:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to bulk reject reviews',
      modifiedCount: 0,
      processed: 0
    };
  }
};

// Same for bulkRemoveReviews
export const bulkRemoveReviews = async (businessUuid, reviewIds, adminId, removalReason) => {
  try {
    console.log('Bulk remove - Input params:', { businessUuid, reviewIds, adminId, removalReason });
    
    if (!Array.isArray(reviewIds)) {
      return {
        success: false,
        error: 'Review IDs must be an array'
      };
    }
    
    const requestData = { 
      reviewIds: reviewIds, 
      adminId: adminId,
      removalReason: removalReason
    };
    
    const resp = await http.post(
      `${URL}/reviews/${businessUuid}/bulk-remove`,
      requestData,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    return {
      success: true,
      ...resp.data
    };
  } catch (error) {
    console.error('Error bulk removing reviews:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to bulk remove reviews',
      modifiedCount: 0,
      processed: 0
    };
  }
};

// Same for bulkRestoreReviews
export const bulkRestoreReviews = async (businessUuid, reviewIds, adminId, restorationNotes) => {
  try {
    console.log('Bulk restore - Input params:', { businessUuid, reviewIds, adminId, restorationNotes });
    
    if (!Array.isArray(reviewIds)) {
      return {
        success: false,
        error: 'Review IDs must be an array'
      };
    }
    
    const requestData = { 
      reviewIds: reviewIds, 
      adminId: adminId,
      restorationNotes: restorationNotes
    };
    
    const resp = await http.post(
      `${URL}/reviews/${businessUuid}/bulk-restore`,
      requestData,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    return {
      success: true,
      ...resp.data
    };
  } catch (error) {
    console.error('Error bulk restoring reviews:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to bulk restore reviews',
      modifiedCount: 0,
      processed: 0
    };
  }
};



// Get review statistics for admin dashboard
export const getReviewStats = async (businessUuid) => {
  try {
    const resp = await http.get(`${URL}/reviews/${businessUuid}/stats`);
    return {
      success: true,
      data: resp.data?.stats || {}
    };
  } catch (error) {
    console.error('Error fetching review stats:', error);
    return {
      success: false,
      data: {
        total: 0,
        approved: 0,
        pending: 0
      }
    };
  }
};


// In frontend services/review.service.js
export const likeReview = async (businessUuid, reviewId, customerId) => {
  try {
    
    const response = await http.post(
      `${URL}/reviews/${businessUuid}/${reviewId}/like`,
      { customerId }
    );
    
    
    // Handle different response structures
    if (response.data) {
      return {
        success: response.data.success === true,
        data: response.data.data || response.data,
        error: response.data.error
      };
    } else if (response.success !== undefined) {
      return {
        success: response.success === true,
        data: response.data || response,
        error: response.error
      };
    }
    
    return {
      success: false,
      error: 'Invalid response format'
    };
  } catch (error) {
    console.error('🔴 Service - API error:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to like review'
    };
  }
};
export const getProductReviews = async (businessUuid, productId, params = {}) => {
  try {
    const { page = 1, limit = 5, sort = 'newest', filter = 'all' } = params;
    const response = await http.get(
      `${URL}/reviews/${businessUuid}/${productId}/public`,
      {
        params: { page, limit, sort, filter }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching reviews:', error);
    return {
      success: false,
      reviews: [],
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalReviews: 0,
        hasMore: false
      }
    };
  }
};

// Get review statistics for a product
export const getProductReviewStats = async (businessUuid, productId) => {
  try {
    const response = await http.get(
      `${URL}/reviews/${businessUuid}/${productId}/stats`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching review stats:', error);
    return {
      success: false,
      data: {
        averageRating: 0,
        totalReviews: 0,
        verifiedReviews: 0,
        withPhotos: 0,
        ratingDistribution: {1: 0, 2: 0, 3: 0, 4: 0, 5: 0}
      }
    };
  }
};

// Get customer's review for a product
export const getCustomerProductReview = async (businessUuid, productId, customerId) => {
  try {
    const resp = await http.get(
      `${URL}/reviews/${businessUuid}/${productId}/customer/${customerId}`
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error fetching customer review:', error);
    return {
      success: false,
      data: null
    };
  }
};

// Add a review (without images)
export const addReview = async (businessUuid, productId, reviewData) => {
  try {
    const resp = await http.post(
      `${URL}/reviews/${businessUuid}/${productId}`,
      reviewData
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error adding review:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to add review'
    };
  }
};



// Get product review details for debugging
export const getReviewDetails = async (businessUuid, productId) => {
  try {
    const resp = await http.get(
      `${URL}/reviews/${businessUuid}/${productId}/details`
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error fetching review details:', error);
    return {
      success: false,
      data: null
    };
  }
};

// Test delivery for debugging
export const testDeliveryCheck = async (businessUuid, productId, customerId) => {
  try {
    const resp = await http.get(
      `${URL}/reviews/${businessUuid}/test-delivery/${productId}/${customerId}`
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error testing delivery check:', error);
    return {
      success: false,
      data: null
    };
  }
};

// Get order statuses for debugging
export const getOrderStatuses = async (businessUuid) => {
  try {
    const resp = await http.get(
      `${URL}/reviews/${businessUuid}/debug/order-statuses`
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error fetching order statuses:', error);
    return {
      success: false,
      data: null
    };
  }
};

// Health check
export const checkReviewServiceHealth = async () => {
  try {
    const resp = await http.get(`${URL}/reviews/health`);
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error checking review service health:', error);
    return {
      success: false,
      data: { status: 'unhealthy' }
    };
  }
};
// Remove an approved review (move back to pending)
export const removeReview = async (businessUuid, reviewId, adminId, removalReason) => {
  try {
    const resp = await http.post(
      `${URL}/reviews/${businessUuid}/${reviewId}/remove`,
      { 
        adminId, 
        removalReason  // Make sure this key matches backend expectation
      }
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error removing review:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to remove review'
    };
  }
};

// Restore a rejected review (move to pending)
export const restoreReview = async (businessUuid, reviewId, adminId, restorationNotes) => {
  try {
    const resp = await http.post(
      `${URL}/reviews/${businessUuid}/${reviewId}/restore`,
      { 
        adminId, 
        restorationNotes  // Make sure this key matches backend expectation
      }
    );
    return {
      success: true,
      data: resp.data
    };
  } catch (error) {
    console.error('Error restoring review:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to restore review'
    };
  }
};



// Export all functions as a service object
export const reviewService = {
  // Basic review operations
  addReview,
  addReviewWithImages,
  getProductReviews,
  getProductReviewStats,
  getCustomerProductReview,
  checkCustomerCanReview,
  likeReview,
  deleteReview,
  
  // Photo operations
  uploadReviewPhoto,
  uploadReviewPhotos,
  removeReviewPhoto,
  
  // Admin operations
  getPendingReviews,
  approveReview,
  rejectReview,
  getAllReviews,
  getReviewStats,
  removeReview,
  restoreReview,
  
  // Bulk admin operations
  bulkApproveReviews,
  bulkRejectReviews,
  bulkRemoveReviews,
  bulkRestoreReviews,
  
  // Settings operations
  getStoreReviewSettings,
  updateStoreReviewSettings,
  
  // Debug/utility operations
  getReviewDetails,
  testDeliveryCheck,
  getOrderStatuses,
  checkReviewServiceHealth,
};